import i18n from '../i18n/config';
// Enhanced Users Page with Complete User Management Features
import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  Stack,
  Switch,
  FormControlLabel,
  Divider,
  RadioGroup,
  Radio,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PersonAdd as InviteIcon,
  Email as EmailIcon,
  MoreVert as MoreVertIcon,
  Refresh as RefreshIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  ManageAccounts as ManageAccountsIcon,
  VpnKey as ResetPasswordIcon,
  Replay as ReInviteIcon,
  Visibility as ViewIcon,
  Archive as ArchiveIcon,
  SupervisorAccount as SuperAdminIcon,
  Person as PersonIcon,
  Notifications as NotificationsIcon,
  Save as SaveIcon,
  SwapHoriz as TransferIcon,
  Security as SecurityIcon,
  AdminPanelSettings as AdminIcon,
  Group as GroupIcon
} from '@mui/icons-material';

import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { useApiCall, useMutation } from '../hooks/useApi';
import { api, userHelpers } from '../services/api';
import NotificationChannelMatrix from '../components/notifications/NotificationChannelMatrix';
import { useNotification } from '../components/common/GlobalNotifications';
import LoadingPage from '../components/common/LoadingPage';
import InviteUser from '../components/auth/InviteUser';
import CreateUserManually from '../components/auth/CreateUserManually';
import { formatRelativeTime } from '../utils/formatters';
import UserNotificationPreferences from '../components/users/UserNotificationPreferences';
import PhoneInput from '../components/common/PhoneInput';
import TimezoneSelect from '../components/common/TimezoneSelect';
import AdvancedDataTable from '../components/common/AdvancedDataTable';

// Define before using in components
const getUserTypeColor = (type) => {
  if (!type) return 'default';
  switch (type.toLowerCase()) {
    case 'superadmin':
      return 'secondary';
    case 'admin':
      return 'error';
    case 'manager':
      return 'warning';
    case 'user':
    default:
      return 'default';
  }
};

const getUserTypeLabel = (type) => {
  const labels = {
    'superadmin': i18n.t('hardcoded.pages_userpage.superadministrador'),
    'admin': i18n.t('inviteUser.roles.admin.label'),
    'manager': i18n.t('inviteUser.roles.manager.label'),
    'user': i18n.t('audit.table.user')
  };
  return labels[type] || type;
};

const getUserTypeIcon = (type) => {
  if (type === 'superadmin') return <SuperAdminIcon />;
  if (type === 'admin') return <AdminIcon />;
  if (type === 'manager') return <GroupIcon />;
  return <PersonIcon />;
};

// Transfer Superadmin Dialog
const TransferSuperadminDialog = ({ open, users, currentUser, onClose, onTransferCompleted }) => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useNotification();
  const [selectedUserId, setSelectedUserId] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const { mutate: transferSuperadmin, loading } = useMutation(
    (data) => api.users.transferSuperadmin(selectedUserId, data),
    {
      onSuccess: () => {
        showSuccess(t('hardcoded.pages_userpage.rol_de_superadmin_transferido_exitosamente'));
        onTransferCompleted();
        handleClose();
      },
      onError: (error) => {
        showError(error.message || t('hardcoded.pages_userpage.error_al_transferir_rol_de'));
      }
    }
  );

  const handleClose = () => {
    setSelectedUserId('');
    setConfirmPassword('');
    onClose();
  };

  const handleTransfer = () => {
    if (!selectedUserId) {
      showError(t('hardcoded.pages_userpage.debes_seleccionar_un_usuario'));
      return;
    }
    if (!confirmPassword) {
      showError(t('hardcoded.pages_userpage.la_contrasena_es_requerida_para'));
      return;
    }
    if (selectedUserId === currentUser?.id) {
      showError(t('hardcoded.pages_userpage.no_puedes_transferir_el_rol'));
      return;
    }

    transferSuperadmin({
      target_user_id: selectedUserId,
      confirmation_password: confirmPassword
    });
  };

  // Filter users that can receive superadmin role (exclude current superadmin)
  const eligibleUsers = users.filter(user =>
    user.id !== currentUser?.id &&
    user.role !== 'superadmin' &&
    user.can_be_modified
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <TransferIcon color="warning" />
          <Typography variant="h6">
            {t('hardcoded.pages_userpage.transferir_rol_de_superadministrador')}</Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>{t('hardcoded.pages_userpage.advertencia')}</strong> {t('hardcoded.pages_userpage.esta_accion_es_irreversible_el')}</Typography>
        </Alert>

        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {t('hardcoded.pages_userpage.transferir_el_rol_de_superadmin')}</Typography>
        </Box>

        {/* User Selection */}
        <Typography variant="h6" gutterBottom>
          {t('hardcoded.pages_userpage.seleccionar_usuario')}</Typography>

        {eligibleUsers.length === 0 ? (
          <Alert severity="info" sx={{ mb: 3 }}>
            {t('hardcoded.pages_userpage.no_hay_usuarios_elegibles_para')}</Alert>
        ) : (
          <Paper sx={{ maxHeight: 300, overflow: 'auto', mb: 3 }}>
            <RadioGroup
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
            >
              <List>
                {eligibleUsers.map((user) => (
                  <ListItem key={user.id} dense>
                    <Radio value={user.id} />
                    <ListItemAvatar>
                      <Avatar>
                        {user.role === 'admin' ? <AdminIcon /> :
                          user.role === 'manager' ? <GroupIcon /> : <PersonIcon />}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={user.full_name || `${user.name} ${user.last_name}`}
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {user.email}
                          </Typography>
                          <Chip
                            label={getUserTypeLabel(user.role)}
                            size="small"
                            color={user.role === 'admin' ? 'error' : user.role === 'manager' ? 'warning' : 'default'}
                          />
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </RadioGroup>
          </Paper>
        )}

        {/* Password Confirmation */}
        <TextField
          fullWidth
          label={t('hardcoded.pages_userpage.confirma_tu_contrasena')}
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          helperText={t('hardcoded.pages_userpage.por_seguridad_confirma_tu_contrasena')}
          required
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>
          {t('common.cancel')}</Button>
        <Button
          onClick={handleTransfer}
          variant="contained"
          color="warning"
          disabled={loading || !selectedUserId || !confirmPassword || eligibleUsers.length === 0}
          startIcon={<TransferIcon />}
        >
          {loading ? t('hardcoded.pages_userpage.transfiriendo') : t('hardcoded.pages_userpage.transferir_superadmin')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// 🆕 NEW: Change Role Dialog
const ChangeRoleDialog = ({ open, user, currentUserRole, onClose, onRoleChanged }) => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useNotification();
  const [newRole, setNewRole] = useState('');

  React.useEffect(() => {
    if (user) {
      setNewRole(user.role || 'user');
    }
  }, [user]);

  const { mutate: updateUserRole, loading } = useMutation(
    (data) => api.users.update(user?.id, data),
    {
      onSuccess: () => {
        showSuccess(t('hardcoded.pages_userpage.rol_cambiado_exitosamente'));
        onRoleChanged();
        onClose();
      },
      onError: (error) => {
        showError(error.message || t('hardcoded.pages_userpage.error_al_cambiar_rol'));
      }
    }
  );

  const handleRoleChange = () => {
    if (newRole === user?.role) {
      onClose();
      return;
    }

    if (newRole === 'superadmin') {
      showError(t('hardcoded.pages_userpage.el_rol_de_superadmin_solo'));
      return;
    }

    // Check permissions
    if (!userHelpers.canUserModifyRole(currentUserRole, newRole)) {
      showError(t('hardcoded.pages_userpage.no_tienes_permisos_para_asignar'));
      return;
    }

    updateUserRole({ role: newRole });
  };

  const roleOptions = [
    { value: 'user', label: t('audit.table.user'), color: 'default', description: t('inviteUser.roles.user.description') },
    { value: 'manager', label: t('inviteUser.roles.manager.label'), color: 'warning', description: t('hardcoded.pages_userpage.puede_gestionar_usuarios_y_configuraciones') },
    { value: 'admin', label: t('inviteUser.roles.admin.label'), color: 'error', description: t('hardcoded.pages_userpage.acceso_completo_al_sistema_excepto') }
  ];

  // Filter available roles based on current user permissions
  const availableRoles = roleOptions.filter(role =>
    userHelpers.canUserModifyRole(currentUserRole, role.value)
  );

  const getUserTypeLabel = (type) => {
    const labels = {
      'superadmin': t('hardcoded.pages_userpage.superadministrador'),
      'admin': t('inviteUser.roles.admin.label'),
      'manager': t('inviteUser.roles.manager.label'),
      'user': t('audit.table.user')
    };
    return labels[type] || type;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ManageAccountsIcon color="primary" />
          <Typography variant="h6">
            {t('hardcoded.pages_userpage.cambiar_rol_de_usuario')}{user?.full_name}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {t('hardcoded.pages_userpage.cambiar_el_rol_del_usuario')}</Typography>
        </Box>

        {/* Current Role */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            {i18n.t('hardcoded.pages_userpage.rol_actual')}</Typography>
          <Chip
            label={getUserTypeLabel(user?.role || 'user')}
            color={userHelpers.getUserRoleColor(user?.role)}
            icon={user?.role === 'superadmin' ? <SuperAdminIcon /> : undefined}
          />
        </Box>

        {/* New Role Selection */}
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>{i18n.t('hardcoded.pages_userpage.nuevo_rol')}</InputLabel>
          <Select
            value={newRole}
            onChange={(e) => setNewRole(e.target.value)}
            label={t('hardcoded.pages_userpage.nuevo_rol')}
          >
            {availableRoles.map(role => (
              <MenuItem key={role.value} value={role.value}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip label={role.label} color={role.color} size="small" />
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Role Description */}
        {newRole && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              {t('hardcoded.pages_userpage.descripcion_del_rol')}</Typography>
            <Typography variant="body2" color="text.secondary">
              {roleOptions.find(r => r.value === newRole)?.description}
            </Typography>
          </Box>
        )}

        {newRole === 'superadmin' && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {t('hardcoded.pages_userpage.el_rol_de_superadmin_solo')}</Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          {t('common.cancel')}</Button>
        <Button
          onClick={handleRoleChange}
          variant="contained"
          disabled={loading || newRole === user?.role || availableRoles.length === 0}
          startIcon={<ManageAccountsIcon />}
        >
          {loading ? t('hardcoded.pages_userpage.cambiando') : t('hardcoded.pages_userpage.cambiar_rol')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Delete User Dialog
const DeleteUserDialog = ({ open, user, onClose, onUserDeleted }) => {
  const { t } = useTranslation();
  const { showSuccess, showError, showWarning } = useNotification();

  const { mutate: deleteUser, loading } = useMutation(
    () => api.users.delete(user?.id),
    {
      onSuccess: () => {
        showSuccess(t('hardcoded.pages_userpage.full_name_ha_sido_desactivado_de', { full_name: user?.full_name }));
        onUserDeleted();
        onClose();
      },
      onError: (error) => {
        showError(error.message || t('hardcoded.pages_userpage.error_al_desactivar_usuario'));
      }
    }
  );

  const handleConfirmDelete = () => {
    if (!user?.id) {
      showError(t('hardcoded.pages_userpage.error_usuario_no_valido'));
      return;
    }
    deleteUser();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <DeleteIcon color="error" />
          <Typography variant="h6">
            {t('hardcoded.pages_userpage.desactivar_usuario')}</Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Alert severity="warning" sx={{ mb: 3 }}>
          {t('hardcoded.pages_userpage.esta_accion_desactivara_al_usuario')}</Alert>

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {t('hardcoded.pages_userpage.deseas_desactivar_a')}</Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 1 }}>
            {user?.full_name} ({user?.email})
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
          {t('hardcoded.pages_userpage.el_usuario_sera_marcado_como')}</Typography>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {t('common.cancel')}</Button>
        <Button
          onClick={handleConfirmDelete}
          variant="contained"
          color="error"
          disabled={loading}
          startIcon={<DeleteIcon />}
        >
          {loading ? t('hardcoded.pages_userpage.desactivando') : t('hardcoded.pages_userpage.desactivar_usuario')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Reset Password Dialog
const ResetPasswordDialog = ({ open, user, onClose, onPasswordReset }) => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useNotification();
  const [sendEmail, setSendEmail] = useState(true);
  const [customPassword, setCustomPassword] = useState('');
  const [useCustomPassword, setUseCustomPassword] = useState(false);

  const { mutate: resetPassword, loading } = useMutation(
    (data) => api.users.resetPassword(user?.id, data),
    {
      onSuccess: (data) => {
        showSuccess(t('hardcoded.pages_userpage.contrasena_restablecida_exitosamente'));
        if (data.new_password && !sendEmail) {
          // Show password in alert if not sending by email
          alert(t('hardcoded.pages_userpage.nueva_contrasena_para_full_name_new_password', { full_name: user?.full_name, new_password: data.new_password }));
        }
        onPasswordReset();
        onClose();
      },
      onError: (error) => {
        showError(error.message || t('hardcoded.pages_userpage.error_al_restablecer_contrasena'));
      }
    }
  );

  const handleReset = () => {
    if (useCustomPassword && !customPassword) {
      showError(t('hardcoded.pages_userpage.debe_ingresar_una_contrasena_personalizada'));
      return;
    }

    resetPassword({
      new_password: useCustomPassword ? customPassword : null,
      send_email: sendEmail
    });
  };

  const validatePassword = (password) => {
    const hasMinLength = password.length >= 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);

    return hasMinLength && hasUpperCase && hasLowerCase && hasNumbers;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ResetPasswordIcon color="warning" />
          <Typography variant="h6">
            {t('hardcoded.pages_userpage.restablecer_contrasena')}{user?.full_name}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {t('hardcoded.pages_userpage.generar_una_nueva_contrasena_para')}</Typography>
        </Box>

        {/* Password Generation Options */}
        <FormControlLabel
          control={
            <Switch
              checked={useCustomPassword}
              onChange={(e) => setUseCustomPassword(e.target.checked)}
            />
          }
          label={t('hardcoded.pages_userpage.usar_contrasena_personalizada')}
          sx={{ mb: 2 }}
        />

        {useCustomPassword ? (
          <TextField
            fullWidth
            label={t('profile.security.newPassword')}
            type="password"
            value={customPassword}
            onChange={(e) => setCustomPassword(e.target.value)}
            sx={{ mb: 2 }}
            helperText={t('hardcoded.pages_userpage.minimo_8_caracteres_incluir_mayusculas')}
            error={customPassword && !validatePassword(customPassword)}
          />
        ) : (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('hardcoded.pages_userpage.se_generara_una_contrasena_automatica')}</Alert>
        )}

        {/* Email Option */}
        <FormControlLabel
          control={
            <Switch
              checked={sendEmail}
              onChange={(e) => setSendEmail(e.target.checked)}
            />
          }
          label={t('hardcoded.pages_userpage.enviar_por_email')}
          sx={{ mb: 1 }}
        />
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
          {t('hardcoded.pages_userpage.el_usuario_recibira_la_nueva')}</Typography>

        <Alert severity="warning" sx={{ mt: 2 }}>
          {t('hardcoded.pages_userpage.esta_accion_generara_una_nueva')}</Alert>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button
          onClick={handleReset}
          variant="contained"
          color="warning"
          disabled={loading || (useCustomPassword && (!customPassword || !validatePassword(customPassword)))}
          startIcon={<ResetPasswordIcon />}
        >
          {loading ? t('hardcoded.pages_userpage.restableciendo') : t('settings.company.reset')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// 🆕 ENHANCED: Edit User Dialog with Role Management
const EditUserDialog = ({ open, user, currentUserRole, onClose, onUserUpdated }) => {
  const { t } = useTranslation();
  const editTheme = useTheme();
  const isMobileEdit = useMediaQuery(editTheme.breakpoints.down('sm'));
  const { hasPermission } = useAuth();
  const { showSuccess, showError } = useNotification();
  const [tabValue, setTabValue] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    last_name: '',
    email: '',
    phone: '',
    language: 'es',
    timezone: 'America/Mexico_City',
    role: 'user'
  });

  React.useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        phone: user.phone || '',
        language: user.language || 'es',
        timezone: user.timezone || 'America/Mexico_City',
        role: user.role || 'user'
      });
    }
  }, [user]);

  const { mutate: updateUser, loading } = useMutation(
    (data) => api.users.update(user?.id, data),
    {
      onSuccess: () => {
        showSuccess(t('hardcoded.pages_userpage.usuario_actualizado_exitosamente'));
        onUserUpdated();
      },
      onError: (error) => {
        showError(error.message || t('hardcoded.pages_userpage.error_al_actualizar_usuario'));
      }
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    const updateData = {};

    Object.keys(formData).forEach(key => {
      if (formData[key] !== (user?.[key] || '')) {
        updateData[key] = formData[key];
      }
    });

    if (Object.keys(updateData).length > 0) {
      updateUser(updateData);
    } else {
      showSuccess(t('hardcoded.pages_userpage.no_hay_cambios_que_guardar'));
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleNotificationPreferencesUpdated = (updatedPreferences) => {
    console.log(t('hardcoded.pages_userpage.notification_preferences_updated'), updatedPreferences);
  };

  const roleOptions = [
    { value: 'user', label: t('audit.table.user'), color: 'default' },
    { value: 'manager', label: t('inviteUser.roles.manager.label'), color: 'warning' },
    { value: 'admin', label: t('inviteUser.roles.admin.label'), color: 'error' }
  ];

  const availableRoles = roleOptions.filter(role =>
    userHelpers.canUserModifyRole(currentUserRole, role.value)
  );

  return (
    // En movil va a pantalla completa: con `maxWidth="lg"` quedaban margenes
    // minimos y la matriz de notificaciones no cabia dentro del dialogo.
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth fullScreen={isMobileEdit}>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <EditIcon color="primary" />
          <Typography variant="h6">
            {t('hardcoded.pages_userpage.editar_usuario')}{user?.full_name}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} variant="scrollable" allowScrollButtonsMobile
            aria-label={t('hardcoded.pages_userpage.user_edit_tabs')}>
            <Tab
              icon={<PersonIcon />}
              label={t('createUser.sections.personalInfo')}
              id="tab-0"
              aria-controls="tabpanel-0"
            />
            {hasPermission('manager') && (
              <Tab
                icon={<NotificationsIcon />}
                label={t('navbar.notifications')}
                id="tab-1"
                aria-controls="tabpanel-1"
              />
            )}
          </Tabs>
        </Box>

        <Box sx={{ p: 3 }}>
          {tabValue === 0 && (
            <Box component="form" onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t('createUser.form.firstName')}
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t('createUser.form.lastName')}
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    required
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t('createUser.form.email')}
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    type="email"
                    required
                  />
                </Grid>

                {/* 🔄 UPDATED: Use PhoneInput component */}
                <Grid item xs={12} md={6}>
                  <PhoneInput
                    value={formData.phone}
                    onChange={(value) => setFormData({ ...formData, phone: value })}
                    label={t('settings.company.phone')}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>{t('createUser.form.language')}</InputLabel>
                    <Select
                      value={formData.language}
                      onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                      label={t('createUser.form.language')}
                    >
                      <MenuItem value="es">{t('profile.languages.es')}</MenuItem>
                      <MenuItem value="en">{t('profile.languages.en')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* 🔄 UPDATED: Use TimezoneSelect component */}
                <Grid item xs={12} md={6}>
                  <TimezoneSelect
                    value={formData.timezone}
                    onChange={(value) => setFormData({ ...formData, timezone: value })}
                    label={t('profile.personalInfo.timezone')}
                    required
                  />
                </Grid>

                {user?.can_be_modified && hasPermission('admin') && availableRoles.length > 0 && (
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>{i18n.t('users.table.role')}</InputLabel>
                      <Select
                        value={formData.role}
                        onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                        label={t('users.table.role')}
                      >
                        {availableRoles.map(role => (
                          <MenuItem key={role.value} value={role.value}>
                            <Chip label={role.label} color={role.color} size="small" />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}

                {(!user?.can_be_modified || !hasPermission('admin')) && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={t('users.table.role')}
                      value={roleOptions.find(r => r.value === user?.role)?.label || user?.role}
                      disabled
                      helperText={t('hardcoded.pages_userpage.no_tienes_permisos_para_cambiar')}
                    />
                  </Grid>
                )}
              </Grid>

              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  startIcon={<SaveIcon />}
                >
                  {loading ? t('sensorForm.saving') : t('hardcoded.pages_userpage.guardar_informacion_personal')}
                </Button>
              </Box>
            </Box>
          )}

          {tabValue === 1 && hasPermission('manager') && user?.id && (
            <UserNotificationPreferences
              userId={user.id}
              userName={user.full_name}
              onPreferencesUpdated={handleNotificationPreferencesUpdated}
            />
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          {t('common.close')}</Button>
      </DialogActions>
    </Dialog>
  );
};

const UsersPage = () => {
  const { user, hasPermission, getCurrentRole } = useAuth();
  const { showSuccess, showError, showWarning } = useNotification();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [createUserOpen, setCreateUserOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedInvitation, setSelectedInvitation] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [transferSuperadminOpen, setTransferSuperadminOpen] = useState(false);
  const [changeRoleOpen, setChangeRoleOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [showArchivedInvitations, setShowArchivedInvitations] = useState(false);
  const [userRoleFilter, setUserRoleFilter] = useState('all');

  const { t } = useTranslation();
  const currentUserRole = getCurrentRole();

  // Debug current user role
  console.log('Current User Info:', {
    user: user,
    currentUserRole: currentUserRole,
    hasAdminPermission: hasPermission('admin'),
    hasManagerPermission: hasPermission('manager'),
    isSuperadmin: currentUserRole === 'superadmin'
  });

  // API calls
  const {
    data: users,
    loading: usersLoading,
    error: usersError,
    refetch: refetchUsers
  } = useApiCall(() => api.users.getAll(false));

  const {
    data: allInvitations,
    loading: invitationsLoading,
    error: invitationsError,
    refetch: refetchInvitations
  } = useApiCall(api.auth.getInvitations);

  // Filter invitations based on archive state
  const invitations = React.useMemo(() => {
    if (!allInvitations) return [];

    return allInvitations.filter(invitation => {
      if (showArchivedInvitations) {
        return invitation.is_used;
      } else {
        return invitation.is_valid;
      }
    });
  }, [allInvitations, showArchivedInvitations]);

  const { mutate: cancelInvitation, loading: cancelLoading } = useMutation(
    userHelpers.cancelInvitation,
    {
      onSuccess: () => {
        showSuccess(t('hardcoded.components_users_pendinginvitations.invitacion_cancelada_exitosamente'));
        refetchInvitations();
        handleMenuClose();
      },
      onError: (error) => {
        showError(error.message || t('hardcoded.pages_userpage.error_al_cancelar_invitacion'));
      }
    }
  );

  const { mutate: createReInvitation, loading: reInviteLoading } = useMutation(
    api.auth.createInvitation,
    {
      onSuccess: () => {
        showSuccess(t('hardcoded.pages_userpage.re-invitacion_enviada_exitosamente'));
        refetchInvitations();
        handleMenuClose();
      },
      onError: (error) => {
        showError(error.message || t('hardcoded.pages_userpage.error_al_enviar_re-invitacion'));
      }
    }
  );

  // Handlers
  const handleInviteUser = () => {
    setInviteOpen(true);
  };

  const handleCreateUser = () => {
    setCreateUserOpen(true);
  };

  const handleInviteCreated = () => {
    refetchInvitations();
    refetchUsers();
  };

  const handleUserCreated = () => {
    refetchUsers();
    refetchInvitations();
  };

  const handleEditUser = (userData) => {
    setSelectedUser(userData);
    setEditDialogOpen(true);
  };

  const handleResetPassword = (userData) => {
    setSelectedUser(userData);
    setResetPasswordOpen(true);
  };

  const handleTransferSuperadmin = (userData) => {
    setSelectedUser(userData);
    setTransferSuperadminOpen(true);
  };

  const handleChangeRole = (userData) => {
    setSelectedUser(userData);
    setChangeRoleOpen(true);
  };

  const handleUserUpdated = () => {
    refetchUsers();
    setSelectedUser(null);
  };

  const handlePasswordReset = () => {
    refetchUsers();
    setSelectedUser(null);
  };

  const handleSuperadminTransferred = () => {
    refetchUsers();
    setSelectedUser(null);
    // Reload the page to update user permissions
    window.location.reload();
  };

  const handleRoleChanged = () => {
    refetchUsers();
    setSelectedUser(null);
  };

  const handleDeleteUser = (userData) => {
    setSelectedUser(userData);
    setDeleteConfirmOpen(true);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setPage(0);
  };

  const handleMenuOpen = (event, invitation) => {
    setAnchorEl(event.currentTarget);
    setSelectedInvitation(invitation);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedInvitation(null);
  };

  const handleCancelInvitation = () => {
    if (selectedInvitation && selectedInvitation.id) {
      cancelInvitation(selectedInvitation.id);
    }
  };

  const handleReInvite = () => {
    if (selectedInvitation) {
      createReInvitation({
        invite_email: selectedInvitation.invite_email,
        user_type: selectedInvitation.user_type,
        expires_in_days: 7
      });
    }
  };

  const handleRefresh = () => {
    refetchUsers();
    refetchInvitations();
    showSuccess(t('hardcoded.pages_userpage.datos_actualizados'));
  };

  const getInvitationStatusColor = (invitation) => {
    if (!invitation) return 'default';
    if (invitation.is_used) return 'success';
    if (invitation.is_expired) return 'error';
    return 'warning';
  };

  const getInvitationStatusText = (invitation) => {
    if (!invitation) return t('hardcoded.components_admin_ordermanagement.pendiente');
    if (invitation.is_used) return t('hardcoded.pages_userpage.usado');
    if (invitation.is_expired) return t('hardcoded.pages_userpage.expirado');
    return t('hardcoded.components_admin_ordermanagement.pendiente');
  };

  if (usersLoading && invitationsLoading) {
    return <LoadingPage message={t('hardcoded.pages_userpage.cargando_usuarios')} />;
  }

  if (!hasPermission('manager')) {
    return (
      <Container>
        <Alert severity="error">
          {t('hardcoded.pages_userpage.no_tienes_permisos_para_acceder')}</Alert>
      </Container>
    );
  }

  const safeUsers = Array.isArray(users) ? users : [];
  const safeInvitations = Array.isArray(invitations) ? invitations : [];
  const filteredUsersByRole = safeUsers.filter((userData) => {
    if (userRoleFilter === 'all') return true;
    return userData.role === userRoleFilter;
  });

  // Count active users (excluding superadmins for the count)
  const activeUsers = safeUsers.length;
  const adminUsers = safeUsers.filter((item) => item.role === 'admin' || item.role === 'superadmin').length;
  const managerUsers = safeUsers.filter((item) => item.role === 'manager').length;
  const pendingInvitations = safeInvitations.filter(i => i && i.is_valid).length;
  const totalInvitations = allInvitations?.length || 0;

  return (
    <Container maxWidth={false} disableGutters>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" fontWeight={700}>
            {t('hardcoded.pages_userpage.gestion_de_usuarios')}</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              size="small"
            >
              {i18n.t('audit.actions.UPDATE')}</Button>
            <Button
              variant="outlined"
              startIcon={<ManageAccountsIcon />}
              onClick={handleCreateUser}
              disabled={createUserOpen}
            >
              {t('createUser.form.createUser')}</Button>
            <Button
              variant="contained"
              startIcon={<InviteIcon />}
              onClick={handleInviteUser}
              disabled={inviteOpen}
            >
              {t('hardcoded.components_users_pendinginvitations.invitar_usuario')}</Button>
          </Box>
        </Box>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent sx={{ cursor: 'pointer' }} onClick={() => setUserRoleFilter('all')}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ p: 1, borderRadius: 1, backgroundColor: 'primary.main', color: 'white' }}>
                  <ManageAccountsIcon />
                </Box>
                <Box>
                  <Typography variant="h4" fontWeight={600}>
                    {activeUsers}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {i18n.t('audit.stats.activeUsers')}</Typography>
                  {userRoleFilter === 'all' && <Chip label={t('hardcoded.pages_userpage.filtro_activo')} size="small" color="primary" sx={{ mt: 1 }} />}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent sx={{ cursor: 'pointer' }} onClick={() => setUserRoleFilter('admin')}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ p: 1, borderRadius: 1, backgroundColor: 'warning.main', color: 'white' }}>
                  <ScheduleIcon />
                </Box>
                <Box>
                  <Typography variant="h4" fontWeight={600}>
                    {adminUsers}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('hardcoded.pages_userpage.admins_superadmins')}
                  </Typography>
                  {userRoleFilter === 'admin' && <Chip label={t('hardcoded.pages_userpage.filtro_activo')} size="small" color="warning" sx={{ mt: 1 }} />}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent sx={{ cursor: 'pointer' }} onClick={() => setUserRoleFilter('manager')}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ p: 1, borderRadius: 1, backgroundColor: 'info.main', color: 'white' }}>
                  <EmailIcon />
                </Box>
                <Box>
                  <Typography variant="h4" fontWeight={600}>
                    {managerUsers}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('hardcoded.pages_userpage.managers')}
                  </Typography>
                  {userRoleFilter === 'manager' && <Chip label={t('hardcoded.pages_userpage.filtro_activo')} size="small" color="info" sx={{ mt: 1 }} />}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Error alerts */}
      {usersError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {t('hardcoded.pages_userpage.error_al_cargar_usuarios')}{usersError.message || t('hardcoded.components_vehicles_vehicledetail.error_desconocido')}
        </Alert>
      )}

      {invitationsError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {t('hardcoded.components_users_pendinginvitations.error_al_cargar_invitaciones')}{invitationsError.message || t('hardcoded.components_vehicles_vehicledetail.error_desconocido')}
        </Alert>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="fullWidth"
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ManageAccountsIcon />
                {i18n.t('hardcoded.pages_systemusagepage.usuarios')}{safeUsers.length})
              </Box>
            }
          />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EmailIcon />
                {t('hardcoded.pages_userpage.invitaciones')}{safeInvitations.length})
                <Switch
                  size="small"
                  checked={showArchivedInvitations}
                  onChange={(e) => setShowArchivedInvitations(e.target.checked)}
                  onClick={(e) => e.stopPropagation()}
                />
                {showArchivedInvitations ? (
                  <Chip label={t('hardcoded.pages_userpage.archivadas')} size="small" variant="outlined" />
                ) : (
                  <Chip label={t('hardcoded.pages_userpage.activas')} size="small" color="primary" />
                )}
              </Box>
            }
          />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <NotificationsIcon />
                Notificaciones
              </Box>
            }
          />
        </Tabs>
      </Paper>

      {/* Content based on selected tab */}
      {tabValue === 0 && (
        // Users Table
        <AdvancedDataTable
          storageKey="users_table"
          rows={filteredUsersByRole}
          rowKey="id"
          initialSortBy="created_at"
          initialSortOrder="desc"
          columns={[
            {
              id: 'full_name',
              label: t('hardcoded.pages_userpage.nombre'),
              accessor: (row) => row.full_name || `${row.name || ''} ${row.last_name || ''}`.trim(),
              filterable: true,
              render: (row) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {getUserTypeIcon(row.role)}
                  <Typography variant="subtitle2" fontWeight={600}>
                    {row.full_name || `${row.name || ''} ${row.last_name || ''}`.trim() || t('hardcoded.pages_alarmprofilespage.sin_nombre')}
                  </Typography>
                </Box>
              )
            },
            { id: 'email', label: t('hardcoded.pages_userpage.email'), accessor: (row) => row.email || '', filterable: true },
            {
              id: 'role',
              label: t('hardcoded.pages_userpage.tipo'),
              accessor: (row) => row.role,
              filterable: true,
              filterType: 'select',
              filterOptions: ['superadmin', 'admin', 'manager', 'user'],
              render: (row) => (
                <Chip label={getUserTypeLabel(row.role)} color={getUserTypeColor(row.role)} size="small" />
              )
            },
            { id: 'phone', label: t('settings.company.phone'), accessor: (row) => row.phone || '-', filterable: true },
            { id: 'timezone', label: t('hardcoded.pages_userpage.zona_horaria'), accessor: (row) => row.timezone || 'America/Mexico_City', filterable: true },
            {
              id: 'last_login_at',
              label: t('hardcoded.pages_userpage.ultimo_ingreso'),
              accessor: (row) => row.last_login_at || '',
              filterable: true,
              render: (row) => row.last_login_at ? formatRelativeTime(row.last_login_at) : t('hardcoded.pages_userpage.sin_ingreso_registrado')
            },
            {
              id: 'created_at',
              label: t('hardcoded.pages_userpage.creado'),
              accessor: (row) => row.created_at,
              filterable: true,
              render: (row) => row.created_at ? formatRelativeTime(row.created_at) : t('hardcoded.pages_userpage.fecha_desconocida')
            }
          ]}
          rowActions={(userData) => (
            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
              <Tooltip title={userData.can_be_modified ? t('hardcoded.pages_userpage.editar_usuario') : t('hardcoded.pages_userpage.ver_usuario')}>
                <IconButton size="small" onClick={() => handleEditUser(userData)}>
                  {userData.can_be_modified ? <EditIcon /> : <ViewIcon />}
                </IconButton>
              </Tooltip>
              {userData.can_be_modified && userData.role !== 'superadmin' && hasPermission('admin') && (
                <Tooltip title={t('hardcoded.pages_userpage.restablecer_contrasena')}>
                  <IconButton size="small" color="warning" onClick={() => handleResetPassword(userData)}>
                    <ResetPasswordIcon />
                  </IconButton>
                </Tooltip>
              )}
              {userData.can_be_modified && userData.role !== 'superadmin' && hasPermission('admin') && (
                <Tooltip title={t('hardcoded.pages_userpage.cambiar_rol')}>
                  <IconButton size="small" color="primary" onClick={() => handleChangeRole(userData)}>
                    <ManageAccountsIcon />
                  </IconButton>
                </Tooltip>
              )}
              {currentUserRole === 'superadmin' && userData.role !== 'superadmin' && userData.can_be_modified && (
                <Tooltip title="Transferir Superadmin">
                  <IconButton size="small" color="secondary" onClick={() => handleTransferSuperadmin(userData)}>
                    <TransferIcon />
                  </IconButton>
                </Tooltip>
              )}
              {userData.can_be_deleted && (
                <Tooltip title={t('hardcoded.pages_userpage.eliminar_usuario')}>
                  <IconButton size="small" color="error" onClick={() => handleDeleteUser(userData)}>
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}
          emptyTitle={t('hardcoded.pages_userpage.no_se_encontraron_usuarios')}
          emptyDescription={t('hardcoded.pages_userpage.ajusta_filtros_o_crea_un')}
        />
      )}

      {tabValue === 1 && (
        // Invitations Table
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{i18n.t('hardcoded.pages_userpage.email_invitado')}</TableCell>
                  <TableCell>{i18n.t('users.table.role')}</TableCell>
                  <TableCell>{i18n.t('gps.table.status')}</TableCell>
                  <TableCell>{t('pages_userpage.table_issuer')}</TableCell>
                  <TableCell>{i18n.t('gps.card.created')}</TableCell>
                  <TableCell>{t('pages_userpage.table_expires')}</TableCell>
                  <TableCell>{i18n.t('hardcoded.pages_userpage.email_enviado')}</TableCell>
                  <TableCell>{t('common.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {safeInvitations.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((invitation) => {
                  if (!invitation || !invitation.id) {
                    return null;
                  }

                  return (
                    <TableRow key={invitation.id} hover>
                      <TableCell>
                        <Typography variant="subtitle2" fontWeight={600}>
                          {invitation.invite_email || invitation.id}
                        </Typography>
                        {invitation.used_by && invitation.used_by.name && (
                          <Typography variant="caption" color="success.main">
                            {t('hardcoded.pages_userpage.usado_por')}{invitation.used_by.name}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getUserTypeLabel(invitation.user_type)}
                          color={getUserTypeColor(invitation.user_type)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getInvitationStatusText(invitation)}
                          color={getInvitationStatusColor(invitation)}
                          size="small"
                          icon={
                            invitation.is_used ? <CheckIcon /> :
                              invitation.is_expired ? <CancelIcon /> :
                                <WarningIcon />
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {invitation.issuer?.name || t('hardcoded.components_schematics_receivereventsmodal.desconocido')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {invitation.issued_at ? (
                          <Tooltip title={new Date(invitation.issued_at).toLocaleString()}>
                            <Typography variant="body2">
                              {formatRelativeTime(invitation.issued_at)}
                            </Typography>
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {i18n.t('hardcoded.pages_userpage.fecha_desconocida')}</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {invitation.expires_at ? (
                          <Tooltip title={new Date(invitation.expires_at).toLocaleString()}>
                            <Typography variant="body2" color={invitation.is_expired ? 'error' : 'text.primary'}>
                              {formatRelativeTime(invitation.expires_at)}
                            </Typography>
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {t('hardcoded.pages_userpage.sin_expiracion')}</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={invitation.email_sent ? t('common.yes') : t('common.no')}
                          color={invitation.email_sent ? 'success' : 'warning'}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {invitation.is_valid && !showArchivedInvitations && (
                          <Tooltip title={t('hardcoded.components_users_pendinginvitations.mas_opciones')}>
                            <IconButton
                              size="small"
                              onClick={(e) => handleMenuOpen(e, invitation)}
                            >
                              <MoreVertIcon />
                            </IconButton>
                          </Tooltip>
                        )}

                        {/* Re-invite button for expired/cancelled invitations */}
                        {(!invitation.is_valid || invitation.is_expired) && !invitation.is_used && (
                          <Tooltip title="Re-invitar">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleReInvite()}
                              disabled={reInviteLoading}
                            >
                              <ReInviteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={safeInvitations.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      )}

      {/* Qué recibe cada usuario de ESTA empresa. El alcance lo impone el
          backend con la empresa activa: aquí no se pide ninguna otra. */}
      {tabValue === 2 && (
        <Stack spacing={1.5}>
          <Alert severity="info">
            Configuración vigente de tu empresa: qué eventos recibe cada usuario y por qué
            canal. Cada quien la ajusta desde su perfil; un administrador puede cambiarla
            desde Editar usuario → Notificaciones.
          </Alert>
          <NotificationChannelMatrix />
        </Stack>
      )}

      {/* Menu for invitation actions */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleReInvite} disabled={reInviteLoading}>
          <ReInviteIcon sx={{ mr: 1 }} />
          Re-invitar
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleCancelInvitation} disabled={cancelLoading}>
          <CancelIcon sx={{ mr: 1 }} />
          {t('hardcoded.pages_userpage.cancelar_invitacion')}</MenuItem>
      </Menu>

      {/* Dialogs */}
      <InviteUser
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onInviteCreated={handleInviteCreated}
      />

      <CreateUserManually
        open={createUserOpen}
        onClose={() => setCreateUserOpen(false)}
        onUserCreated={handleUserCreated}
      />

      <EditUserDialog
        open={editDialogOpen}
        user={selectedUser}
        currentUserRole={currentUserRole}
        onClose={() => setEditDialogOpen(false)}
        onUserUpdated={handleUserUpdated}
      />

      <ResetPasswordDialog
        open={resetPasswordOpen}
        user={selectedUser}
        onClose={() => setResetPasswordOpen(false)}
        onPasswordReset={handlePasswordReset}
      />

      <TransferSuperadminDialog
        open={transferSuperadminOpen}
        users={safeUsers}
        currentUser={user}
        onClose={() => setTransferSuperadminOpen(false)}
        onTransferCompleted={handleSuperadminTransferred}
      />

      <ChangeRoleDialog
        open={changeRoleOpen}
        user={selectedUser}
        currentUserRole={currentUserRole}
        onClose={() => setChangeRoleOpen(false)}
        onRoleChanged={handleRoleChanged}
      />

      <DeleteUserDialog
        open={deleteConfirmOpen}
        user={selectedUser}
        onClose={() => setDeleteConfirmOpen(false)}
        onUserDeleted={() => {
          refetchUsers();
          setSelectedUser(null);
        }}
      />
    </Container>
  );
};

export default UsersPage;